import { useCallback, useEffect, useMemo, useState } from "react";
import { useGrpcClient, usePropertyGrpcCall } from "../../../lib/grpc";
import { MealPlanAPIPromiseClient } from "../../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_grpc_web_pb";
import { GetAllMealPlansRequest } from "../../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_pb";
import { useProperty } from "../../../components/property-provider";
import { useAccommodationsQuery } from "../../accommodation/accommodation.hook";

export interface InventoryOption {
  value: string;
  label: string;
}

export function useInventoryOptions() {
  const property = useProperty();
  const [mealPlans, setMealPlans] = useState<InventoryOption[]>([]);
  const [loadingMealPlans, setLoadingMealPlans] = useState(false);

  const {
    data: accommodationsData = [],
    isFetching: loadingAccommodations,
    refetch: reloadAccommodations,
  } = useAccommodationsQuery();

  const accommodations = useMemo<InventoryOption[]>(
    () =>
      accommodationsData.map(obj => {
        const name = obj.translationsList?.length > 0 ? obj.translationsList[0].name : obj.ticker;
        return { value: obj.ticker, label: `${obj.ticker} - ${name}` };
      }),
    [accommodationsData]
  );

  const mealPlanClient = useGrpcClient(MealPlanAPIPromiseClient);
  const getAllMealPlans = usePropertyGrpcCall(mealPlanClient, mealPlanClient.getAllMealPlans);

  const loadMealPlans = useCallback(async () => {
    setLoadingMealPlans(true);
    try {
      const request = new GetAllMealPlansRequest();
      request.setPropertyTicker(property.ticker);
      const response = await getAllMealPlans(request);
      const options: InventoryOption[] = response.getMealPlansList().map(mp => {
        const obj = mp.toObject();
        const name = obj.translationsList?.length > 0 ? obj.translationsList[0].name : obj.ticker;
        return { value: obj.ticker, label: `${obj.ticker} - ${name}` };
      });
      setMealPlans(options);
    } catch (error) {
      console.error("Error loading meal plans:", error);
      setMealPlans([]);
    } finally {
      setLoadingMealPlans(false);
    }
  }, [getAllMealPlans, property.ticker]);

  useEffect(() => {
    loadMealPlans().catch(console.error);
  }, [loadMealPlans]);

  return {
    accommodations,
    mealPlans,
    loadingAccommodations,
    loadingMealPlans,
    reloadAccommodations,
    reloadMealPlans: loadMealPlans,
  };
}
